/**
 * src/hooks/useAgentTeleportMode.ts
 *
 * Cinematic "AI Agent Teleport Mode" controller.
 *
 * High-level state machine (one selected agent at a time):
 *
 *   idle
 *     │  start(agentPk)
 *     ▼
 *   entering ── moveend ──▶ arrived ──▶ rotating ◀──┐
 *                                         │         │ position update
 *                                         ▼         │ queued
 *                                       countdown ──┘
 *                                         │ countdown == 0
 *                                         ▼
 *                                       flying ─── moveend ──▶ arrived
 *                                         │
 *                                         │ exit() at any time
 *                                         ▼
 *                                       exiting ── moveend ──▶ idle
 *
 * Following rules
 * ───────────────
 *  * The hook never holds React state for things that change every
 *    animation frame (bearing during rotation, queued positions while
 *    flying). Those live in refs so the parent component only
 *    re-renders on phase / countdown / selection changes — markers
 *    keep humming along.
 *  * Every `setTimeout`, `setInterval`, and `requestAnimationFrame`
 *    handle is parked in a ref and cleared on phase change AND on
 *    unmount. The `enabled()` guard on `safeFlyTo` / `safeEaseTo`
 *    catches the (rare) race where a timer fires between cleanup and
 *    the next render.
 *  * Manual override ("Free Look") halts auto-camera moves but
 *    keeps the queue updated, so toggling it back to follow resumes
 *    smoothly from the latest known position.
 *  * Exits cleanly on Escape, on user drag/zoom (unless override is
 *    on), and on imperative `exit()` calls. Always restores the
 *    pre-teleport camera with an easeTo.
 *
 * The hook is intentionally renderless — it only emits state. The
 * companion `<AgentTeleportHUD />` component does all the DOM work
 * and the route-line layer is managed inline here so the GeoJSON
 * source and the queued position can never get out of sync.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as MapboxGl from 'mapbox-gl';

import { ESCAPE_PRIORITY, useEscapeOwner } from '../utils/escapeStack';

import {
  type AgentListEntry,
} from '../services/agentPositionService';
import {
  buildingAwareZoom,
  calculateBearing,
  captureCamera,
  ensure3DBuildingsLayer,
  getTeleportDuration,
  haversineDistanceKm,
  isLongHop,
  maxRenderedBuildingHeightM,
  remove3DBuildingsLayer,
  safeEaseTo,
  safeFlyTo,
  type CameraSnapshot,
  type LngLat,
} from '../utils/mapAnimation';
import {
  openTeleportSession,
  type TeleportSessionHandle,
  type TeleportFrame,
  type TeleportRole,
  type TeleportPresenceMember,
  type TeleportCameraOptions,
} from '../services/teleportSessionService';
import {
  searchAndGeocodePlace,
  type TeleportPlaceOutcome,
} from '../services/teleportPlaceSearch';
import {
  reverseGeocodeLatLng,
  primeReverseCache,
  getCachedReverseName,
} from '../services/teleportReverseGeocode';
import { getMovementStatus } from '../services/agentMovementService';

export type TeleportPhase =
  | 'idle'
  | 'entering'
  | 'flying'
  | 'arrived'
  | 'rotating'
  | 'countdown'
  | 'exiting';

export type TeleportExitReason =
  | 'user'
  | 'escape'
  | 'user_interaction'
  | 'agent_gone'
  | 'unmount'
  | 'agent_changed';

export interface AgentTeleportPanelInfo {
  agentPk:      string;
  displayName:  string | null;
  countryCode:  string | null;
  position:     LngLat;
}

export interface UseAgentTeleportModeOpts {
  map:    MapboxGl.Map | null;
  /** Latest snapshot of the agent positions visible on the map. */
  agents: AgentListEntry[];
  /**
   * Wallet public key of the local user. Sent to the teleport-
   * session WS hub on join so the HUD on every client can show
   * "@G…XYZ is hosting". Optional — anonymous joiners are fine.
   */
  walletPk?: string | null;
  /**
   * Optional notification callback, fired any time the mode exits
   * (clean restore is already done by the time this fires). Useful
   * for parent UIs that want to clear their "selected agent" pill.
   */
  onExited?: (reason: TeleportExitReason) => void;
  /**
   * The current Mapbox style URL, as known by the parent (e.g.
   * App.tsx's `mapStyle` state). Optional — only required if you
   * want the teleport HUD's "Quick style" buttons (Dark / Satellite
   * / Original). Used in two places:
   *   1. Snapshotted on `enterMode` so the "Original" button can
   *      restore whatever style was active when the cinematic
   *      started, regardless of what the user toggles to during it.
   *   2. Surfaced as `currentMapStyle` so the HUD can highlight
   *      whichever style chip is active.
   */
  mapStyle?: string | null;
  /**
   * Notify the parent when the teleport HUD switches the map style.
   * The hook also calls `map.setStyle(url)` itself; this callback
   * lets the parent keep its mirror state (e.g. the SettingsOverlay
   * dropdown) in sync. Fired exactly once per `setMapStyle()` call.
   */
  onMapStyleChange?: (url: string) => void;
}

/**
 * Structured place label the hook surfaces for the HUD's "Arrived"
 * + "Next teleport" overlays. Split into hierarchy levels so the
 * HUD can render a primary line (city/locality) and a secondary
 * line (state · country) without having to parse a single string.
 */
export interface TeleportStopLabel {
  /** Primary label — typically city / locality (e.g. "Manhattan"). */
  short:   string;
  /**
   * State / region SHORT code without the country prefix
   * ("NY", "CA", "ON"). Empty string when not resolved.
   */
  region:  string;
  /**
   * Country ISO 3166-1 alpha-2 ("US", "JP"). Empty string when
   * not resolved.
   */
  country: string;
}

export interface AgentTeleportApi {
  // ── Public state (drives the HUD) ────────────────────────────────
  /** Agent the user is *previewing* in the click panel before confirming. */
  pendingAgentId:   string | null;
  /** Agent currently being followed. Distinct from pendingAgentId so the
   *  click panel can be open while the previous flight is unwinding. */
  selectedAgentId:  string | null;
  enabled:          boolean;
  phase:            TeleportPhase;
  countdownSeconds: number | null;
  currentStopIndex: number;
  manualOverride:   boolean;
  /** The next queued stop (camera will fly here when phase = flying). */
  nextDestination:  LngLat | null;
  /**
   * Number of pending stops in the FIFO. Includes the in-flight
   * target while phase = entering / flying. Useful for the HUD's
   * "N stops queued" pill so the user can see they've fallen
   * behind a chatty agent.
   */
  queueLength:      number;
  /** Latest snapshot of the selected agent (for HUD pill / arrival panel). */
  selectedAgent:    AgentListEntry | null;
  /** Convenience aggregate for the click panel. */
  pendingPanel:     AgentTeleportPanelInfo | null;

  /**
   * Where the SELECTED agent's marker should render on the map.
   *
   *   - In teleport mode: this lags the WS-reported live position so
   *     the marker stays at the last visited stop until the cinematic
   *     camera arrives at the next one. That keeps marker + camera +
   *     viewers locked in step (the user's ask: "we should handle the
   *     timing of transition").
   *   - Outside teleport mode: `null` (caller falls back to live WS).
   *   - When the host enqueues a manual place, the marker visits it
   *     too — this position updates in `onArrived` regardless of
   *     whether the stop came from MCP or the search box.
   *
   * Caller usage: pass through to AgentMarkerLayer's
   * `positionOverrides` prop. Only applies to `selectedAgentId`.
   */
  displayedAgentPosition: LngLat | null;
  /**
   * Reverse-geocoded structured display name for the stop the camera
   * is CURRENTLY sitting on. Drives the "Arrived: <city>" overlay.
   *   - `short`:   primary label (city / locality / neighborhood)
   *   - `region`:  state/province short_code ("NY", "CA", "ON")
   *   - `country`: ISO 3166-1 alpha-2 ("US", "JP")
   * `null` while the lookup is in flight or when Mapbox couldn't
   * resolve anything; the HUD should fall back to coords in that
   * case. `region` / `country` may be empty strings even when
   * `short` is populated (e.g. for features in countries that
   * don't have a region tier).
   */
  currentStopName:        TeleportStopLabel | null;
  /**
   * Same shape as `currentStopName` but for the NEXT queued stop
   * (the one the countdown is counting down to). Drives the
   * "Next teleport: <city>" line under the countdown overlay.
   */
  nextStopName:           TeleportStopLabel | null;

  /**
   * `true` when the host has explicitly paused the cinematic. Distinct
   * from `manualOverride`: pause is a per-session "stop visiting new
   * stops" signal that gets PUBLISHED to viewers (so they see a
   * "Host paused" badge), whereas manualOverride is the host's local
   * "let me drag the map without losing my place in the queue" toggle.
   * Both prevent auto-camera-moves; pause additionally prevents the
   * countdown from being kicked when a new stop arrives.
   */
  paused:           boolean;
  /**
   * Mirror of the host's `paused` for VIEWERS: tracks whatever value
   * the host last broadcast. `null` for the host themselves (they
   * read their own `paused` field) and for solo sessions.
   */
  hostPaused:       boolean | null;

  // ── Multi-viewer ("watch party") state ───────────────────────────
  /**
   * The local user's role in the active session. `null` while no
   * session is open. The hub assigns the first joiner per agentPk
   * as `host` and auto-promotes the longest-tenured viewer if the
   * host disconnects, so this can flip from `viewer` → `host`
   * mid-session — the HUD should react with a toast.
   */
  role:             TeleportRole | null;
  /** Server-assigned id for the local connection (`null` until welcome arrives). */
  selfId:           string | null;
  /** Server-assigned id of the current host (`null` if no session). */
  hostId:           string | null;
  /**
   * Wallet public key of the current host (if they sent one with
   * their `join`). May be `null` for anonymous hosts.
   */
  hostWalletPk:     string | null;
  /**
   * Everyone in the session including the local user, sorted by
   * joinedAt ascending so `viewers[0]` is the host (or the longest-
   * tenured member while role-promotion is in flight). Useful for
   * the "watching with N others" pill.
   */
  viewers:          TeleportPresenceMember[];

  // ── Imperative controls ──────────────────────────────────────────
  requestPanel:      (agentPk: string) => void;
  dismissPanel:      () => void;
  start:             (agentPk?: string) => void;
  exit:              (reason?: TeleportExitReason) => void;
  setManualOverride: (next: boolean) => void;
  /**
   * Look up a free-text place ("Times Square", "Eiffel Tower",
   * "tallest building in Tokyo") and append it to the teleport
   * queue. Cache-first via GeoLink so a fleet that has visited the
   * same place before pays zero Mapbox calls; Mapbox forward-
   * geocoding is the fallback, with a fire-and-forget write back
   * to GeoLink so the next user reuses the result.
   *
   * Resolves with the geocoder outcome so the HUD can show
   * "Queued: <place>" or surface a friendly error toast. Only
   * available to the active host — viewers receive the queue
   * frame via the WS hub once the host enqueues. (`ok: false`
   * with `code: 'no_query'` is returned for non-host callers
   * to keep the UI happy without leaking auth-style errors.)
   */
  enqueuePlace:      (query: string) => Promise<TeleportPlaceOutcome>;
  /**
   * Direct-enqueue an already-resolved (lat, lng, place_name) — used
   * by the HUD's autocomplete dropdown when the user clicks a
   * suggestion (skipping the cache-first round trip in `enqueuePlace`
   * because we already HAVE the resolved coords). Primes the reverse-
   * geocode cache too so the "Arrived" badge surfaces the chosen
   * place_name when the camera lands.
   */
  enqueueLngLat:     (pos: LngLat, placeName?: string | null) => void;
  /**
   * Toggle the host's "pause cinematic" state. Pausing keeps the WS
   * session open + viewers connected, but stops auto-advancing through
   * queued stops; manually-added places still queue but don't fly
   * until the host resumes. Also publishes the pause state so viewers
   * see a "Host paused" badge instead of guessing why nothing's
   * happening. No-op for viewers.
   */
  setPaused:         (next: boolean) => void;
  /**
   * Live-swap the Mapbox style URL. Fires `map.setStyle(url)`,
   * notifies the parent via the optional `onMapStyleChange` opt,
   * and waits on `style.load` to re-apply the cinematic's bespoke
   * layers (3D buildings, route line) — without this re-application
   * the new style would render but the route line would vanish and
   * 3D buildings would no longer extrude.
   *
   * Available on every role (it's a local-camera change; viewers
   * see their own map's style update without affecting the host).
   * No-op when no style is provided or when the new url matches
   * the current one.
   */
  setMapStyle:       (url: string) => void;
  /**
   * The Mapbox style URL active at the moment teleport mode entered
   * (snapshot of the `mapStyle` opt at `enterMode` time). The HUD
   * uses this for the "Original" style chip — it's "go back to
   * whatever I had before opening teleport". `null` when teleport
   * hasn't entered yet or when no `mapStyle` was provided.
   */
  originalMapStyle:  string | null;
  /**
   * Mirror of the `mapStyle` opt — surfaced on the API so the HUD's
   * style-chip highlighting can reflect the current value without
   * having to thread the parent state through as a separate prop.
   */
  currentMapStyle:   string | null;
}

// ── Constants ────────────────────────────────────────────────────────
const ENTER_ZOOM        = 17;
const ENTER_PITCH       = 60;
// Countdown cadence — 3 seconds matches the GeoAI fleet runner's
// 12s-per-stop tempo: ~12s observation → 3s countdown → ~3-4s
// flight = camera lands ~6-8s after the agent moves, which is the
// shortest gap before the agent moves AGAIN. Any longer and we'd
// always be flying to a stop the agent already left; any shorter
// and the user has no anticipation window.
const COUNTDOWN_SECONDS = 3;
const ROTATE_STEP_DEG   = 30;
const ROTATE_STEP_MS    = 1500;
const ROTATE_MAX_STEPS  = 24;     // hard stop after two full rotations even if no new pos
const POSITION_DELTA_M  = 8;      // ignore agent updates this small (jitter / GPS noise)
const MAX_QUEUE_LENGTH  = 8;      // cap on pending stops (prevents runaway with chatty agents)

// Cosmetic constants for the teleport route line. Single source of
// truth so the HUD legend (if we add one) and the actual line stay
// aligned.
const ROUTE_SOURCE_ID = 'gt-teleport-route';
const ROUTE_LAYER_ID  = 'gt-teleport-route-line';

function metersBetween(a: LngLat, b: LngLat): number {
  return haversineDistanceKm(a, b) * 1000;
}

function findAgent(agents: AgentListEntry[], pk: string | null): AgentListEntry | null {
  if (!pk) return null;
  for (const a of agents) if (a.agent_pk === pk) return a;
  return null;
}

export function useAgentTeleportMode(opts: UseAgentTeleportModeOpts): AgentTeleportApi {
  const { map, agents, walletPk, onExited, mapStyle, onMapStyleChange } = opts;

  // ── Public React state ─────────────────────────────────────────────
  const [pendingAgentId,   setPendingAgentId]   = useState<string | null>(null);
  const [selectedAgentId,  setSelectedAgentId]  = useState<string | null>(null);
  const [enabled,          setEnabled]          = useState(false);
  const [phase,            setPhase]            = useState<TeleportPhase>('idle');
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [manualOverride,   setManualOverride]   = useState(false);
  const [nextDestination,  setNextDestination]  = useState<LngLat | null>(null);

  // ── Multi-viewer ("watch party") React state ───────────────────────
  const [role,         setRole]         = useState<TeleportRole | null>(null);
  const [selfId,       setSelfId]       = useState<string | null>(null);
  const [hostId,       setHostId]       = useState<string | null>(null);
  const [viewers,      setViewers]      = useState<TeleportPresenceMember[]>([]);

  // ── Cinematic-controlled marker / place-name React state ───────────
  // The position the SELECTED agent's marker should render at while
  // teleport mode is active. Lags the live WS position so the
  // marker, the camera, and every viewer's camera all visit the
  // same stop at the same time (driven by `cameraStopRef`).
  const [displayedAgentPosition, setDisplayedAgentPosition] = useState<LngLat | null>(null);
  // Reverse-geocoded place labels for the current/next stops. Best
  // effort — both can be `null` while the lookup is in flight or
  // when Mapbox returned nothing useful. Structured (short / region
  // / country) so the HUD can render a two-line overlay without
  // having to parse a single string.
  const [currentStopName, setCurrentStopName] = useState<TeleportStopLabel | null>(null);
  const [nextStopName,    setNextStopName]    = useState<TeleportStopLabel | null>(null);
  // Host's pause toggle (and a viewer-side mirror of the host's value).
  const [paused,     setPaused]     = useState(false);
  const [hostPaused, setHostPaused] = useState<boolean | null>(null);

  // Snapshot of `opts.mapStyle` at the moment teleport mode entered —
  // drives the HUD's "Original" style chip ("go back to whatever I
  // had before opening teleport"). Cleared on exit so a brand-new
  // session captures a fresh snapshot. Not put in a ref because the
  // HUD wants to react when it changes (highlight the active chip).
  const [originalMapStyle, setOriginalMapStyle] = useState<string | null>(null);

  // ── Internal refs (do not trigger re-renders) ──────────────────────
  const phaseRef            = useRef<TeleportPhase>('idle');
  const enabledRef          = useRef(false);
  const selectedAgentIdRef  = useRef<string | null>(null);
  const manualOverrideRef   = useRef(false);
  const cameraSnapshotRef   = useRef<CameraSnapshot | null>(null);
  const cameraStopRef       = useRef<LngLat | null>(null);          // where the camera last landed
  // FIFO of stops we want to visit. Pushed by every meaningful
  // agent position update; the front is the *next* stop the camera
  // will fly to. Capped at MAX_QUEUE_LENGTH so a fleet that posts
  // a position every second can't grow this without bound — when
  // the queue is full we drop the second-oldest entry (front is the
  // active target so dropping it would visibly skip), keeping the
  // most recent path. Empty queue = "we're caught up to the agent".
  const queueRef            = useRef<LngLat[]>([]);
  // Convenience: the position React state mirror so the HUD can show
  // queue depth without us doing manual setState plumbing on every
  // enqueue/dequeue.
  const [queueLength, setQueueLength] = useState(0);
  const lastPositionRef     = useRef<LngLat | null>(null);          // for bearing calc
  const rotateStepCountRef  = useRef(0);
  const rotateBearingRef    = useRef(0);
  const userInteractedRef   = useRef(false);
  // All disposable timers / event-listener removers live here so the
  // cleanup loop can clear them without enumerating each one.
  const disposersRef        = useRef<Array<() => void>>([]);

  // ── Multi-viewer refs ──────────────────────────────────────────────
  // The active WS handle for the watch-party hub. Replaced on each
  // start() and closed on exit(). Held in a ref because callbacks
  // close over their first render's snapshot — using state would
  // make a stale handle leak past start().
  const sessionRef          = useRef<TeleportSessionHandle | null>(null);
  // Mirror of `role` state for same-tick checks inside camera /
  // phase helpers (same lockstep concern as phaseRef vs. phase).
  const roleRef             = useRef<TeleportRole | null>(null);
  // Set true once welcome has arrived for the current session, so
  // the entry-flight watchdog (described in enterMode below) knows
  // not to fall back to "assume host alone".
  const welcomeReceivedRef  = useRef(false);
  // The function the WS welcome handler calls to actually fire the
  // entry flyTo as host. enterMode() stashes this here BEFORE it
  // opens the WS so the welcome arrival has something to invoke.
  const dispatchEntryRef    = useRef<(() => void) | null>(null);
  // Per-session guard so dispatchEntry() can only fire ONCE per
  // teleport. The WS welcome handler and the offline-mode watchdog
  // can both race to invoke it and a duplicate flyTo would register
  // a second `map.once('moveend', onArrived)` listener — both fire
  // on the next moveend, double-shifting the queue and silently
  // collapsing rotation/follow into a no-op.
  const dispatchedThisSessionRef = useRef(false);

  // Latest-callback refs. These are how startCountdown invokes the
  // CURRENT beginFlight rather than the stale first-render version,
  // and how the rotation tick / position-update reaction invoke the
  // CURRENT startCountdown.
  //
  // Why this is necessary
  // ─────────────────────
  // useCallback memoises by dep-array. `startCountdown`'s only
  // declared dep is the (stable) `schedule`, so it's created EXACTLY
  // ONCE — on the first render, before the Mapbox map has loaded
  // (`map = null` at that point). The `beginFlight` it captured at
  // that moment also closed over the null map. By the time the user
  // clicks Start Teleport — many renders later, with the map fully
  // loaded — `beginFlight` has been regenerated dozens of times to
  // pick up the live `map`, but `startCountdown` is still calling
  // the original null-map version. Result: every countdown completion
  // logs `[teleport] beginFlight: ABORT no map` and the camera
  // freezes mid-cinematic.
  //
  // Adding `beginFlight` (and friends) to startCountdown's deps would
  // create a circular cycle (startCountdown → beginFlight →
  // startRotation → startCountdown) that React resolves by reusing
  // an old function — undoing the fix. Refs sidestep the cycle by
  // letting the call site read the latest value at INVOKE time.
  const beginFlightRef     = useRef<() => void>(() => {});
  const startCountdownRef  = useRef<() => void>(() => {});
  const startRotationRef   = useRef<() => void>(() => {});
  // Same pattern for refreshNextStopName — called from enqueueStop /
  // shiftStop (both stable useCallbacks with [] deps that capture
  // their first-render closure of refreshNextStopName, which itself
  // closes over `setNextStopName` from the first render — all stable
  // anyway, but a ref keeps us bug-free if anyone later widens the
  // dependency surface of refreshNextStopName).
  const refreshNextStopNameRef = useRef<() => void>(() => {});

  // Mirror state into refs so callbacks (which close over their first
  // render's snapshot) always see the latest values. Belt-and-
  // suspenders alongside the *Sync setters below — these effects
  // catch the (rare) case where state is updated by a path that
  // bypassed the sync setters (e.g. a future contributor adds a
  // setPhase(...) directly).
  useEffect(() => { phaseRef.current           = phase; },           [phase]);
  useEffect(() => { enabledRef.current         = enabled; },         [enabled]);
  useEffect(() => { selectedAgentIdRef.current = selectedAgentId; }, [selectedAgentId]);
  useEffect(() => { manualOverrideRef.current  = manualOverride; },  [manualOverride]);
  useEffect(() => { roleRef.current            = role; },            [role]);
  // Same ref-mirroring trick for `paused` so animationGate and the
  // position-update reaction (both read sync inside callbacks that
  // close over earlier render's snapshot) see the latest value.
  const pausedRef = useRef(false);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // ── Synchronous state-and-ref setters ──────────────────────────────
  // The ref-mirroring useEffects above only fire AFTER React commits,
  // which happens after the current synchronous block returns. So
  // calling `setPhase('entering')` followed immediately by
  // `safeFlyTo()` (which checks `phaseRef.current` via animationGate)
  // would see the stale 'idle' phase and reject the camera move.
  // These setters keep the ref in lockstep so any same-tick gate
  // check sees the new value.
  const setPhaseSync = useCallback((next: TeleportPhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);
  const setEnabledSync = useCallback((next: boolean) => {
    enabledRef.current = next;
    setEnabled(next);
  }, []);
  const setSelectedAgentIdSync = useCallback((next: string | null) => {
    selectedAgentIdRef.current = next;
    setSelectedAgentId(next);
  }, []);
  const setManualOverrideSync = useCallback((next: boolean) => {
    manualOverrideRef.current = next;
    setManualOverride(next);
  }, []);
  const setRoleSync = useCallback((next: TeleportRole | null) => {
    roleRef.current = next;
    setRole(next);
  }, []);

  // Latest snapshot of the selected agent — re-derived on every
  // agents update. Cheap; the array is small (≤ a few hundred).
  const selectedAgent = useMemo(
    () => findAgent(agents, selectedAgentId),
    [agents, selectedAgentId],
  );

  const hostWalletPk = useMemo<string | null>(() => {
    if (!hostId) return null;
    const m = viewers.find((v) => v.id === hostId);
    return m ? (m.walletPk || null) : null;
  }, [hostId, viewers]);

  const pendingPanel = useMemo<AgentTeleportPanelInfo | null>(() => {
    if (!pendingAgentId) return null;
    const a = findAgent(agents, pendingAgentId);
    if (!a) return null;
    return {
      agentPk:     a.agent_pk,
      displayName: a.displayName,
      countryCode: a.country_code || null,
      position:    { lat: a.lat, lng: a.lng },
    };
  }, [agents, pendingAgentId]);

  // ── Disposer plumbing ──────────────────────────────────────────────
  const addDisposer = useCallback((fn: () => void) => {
    disposersRef.current.push(fn);
  }, []);

  // ── Queue helpers ──────────────────────────────────────────────────
  // Push a stop onto the FIFO with three guards:
  //   1. If the new stop is within POSITION_DELTA_M of the QUEUE'S
  //      LAST entry, drop it (de-jitter — agents post throttled
  //      "I'm still here" frames every ~1.5 s).
  //   2. If the queue is empty AND the new stop is within
  //      POSITION_DELTA_M of where the camera *currently* sits,
  //      drop it too (we're already there; don't kick a countdown
  //      to fly to ourselves).
  //   3. If the queue is full, drop the SECOND oldest entry — the
  //      front is the active fly target so dropping it would visibly
  //      skip a stop, but keeping it preserves cinematic continuity.
  const enqueueStop = useCallback((pos: LngLat) => {
    const q = queueRef.current;
    if (q.length > 0) {
      const last = q[q.length - 1];
      if (metersBetween(last, pos) < POSITION_DELTA_M) return;
    } else if (cameraStopRef.current
        && metersBetween(cameraStopRef.current, pos) < POSITION_DELTA_M) {
      return;
    }
    q.push(pos);
    while (q.length > MAX_QUEUE_LENGTH) {
      q.splice(1, 1);   // drop second-oldest (preserve in-flight target at index 0)
    }
    setQueueLength(q.length);
    if (roleRef.current === 'host') {
      sessionRef.current?.publishQueue([...q]);
    }
    // Surface the new front-of-queue name if this enqueue created it
    // (e.g. the queue was empty when the user typed in a place).
    refreshNextStopNameRef.current?.();
  }, []);

  const peekStop = useCallback((): LngLat | null => {
    return queueRef.current.length > 0 ? queueRef.current[0] : null;
  }, []);

  const shiftStop = useCallback((): LngLat | null => {
    const out = queueRef.current.shift() ?? null;
    setQueueLength(queueRef.current.length);
    if (roleRef.current === 'host') {
      sessionRef.current?.publishQueue([...queueRef.current]);
    }
    refreshNextStopNameRef.current?.();
    return out;
  }, []);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    setQueueLength(0);
  }, []);

  // ── Host-only WS publish helpers ───────────────────────────────────
  // Each one is a no-op when the local user is a viewer (or no
  // session is open). Centralises the role-check so call sites can
  // just say "publish this" without litter.
  const publishCameraAction = useCallback((
    action:  'flyTo' | 'easeTo' | 'jumpTo' | 'stop',
    options?: TeleportCameraOptions,
  ) => {
    if (roleRef.current !== 'host') return;
    sessionRef.current?.publishCameraAction(action, options);
  }, []);
  const publishPhase = useCallback((
    nextPhase: TeleportPhase,
    nextStopIndex: number,
    nextCountdown: number | null,
  ) => {
    if (roleRef.current !== 'host') return;
    sessionRef.current?.publishPhase(nextPhase, nextStopIndex, nextCountdown);
  }, []);
  // Belt-and-suspenders: publish a phase frame on every meaningful
  // host-side state change so a freshly-promoted viewer (post-host
  // disconnect) sees the most recent state on its next welcome.
  useEffect(() => {
    if (roleRef.current !== 'host' || !enabled) return;
    sessionRef.current?.publishPhase(phase, currentStopIndex, countdownSeconds);
  }, [phase, currentStopIndex, countdownSeconds, enabled, role]);

  const clearDisposers = useCallback(() => {
    const list = disposersRef.current;
    disposersRef.current = [];
    for (const fn of list) {
      try { fn(); } catch { /* never let one bad listener kill cleanup */ }
    }
  }, []);

  // Helper: schedule a setTimeout that auto-registers itself as a
  // disposer. Returns the handle so callers can clear it early if
  // a phase transition supersedes it.
  const schedule = useCallback((fn: () => void, ms: number): ReturnType<typeof setTimeout> => {
    const handle = setTimeout(() => {
      // Defence-in-depth: disposer might have already wiped us, but
      // if it didn't, we still don't want to fire after exit/unmount.
      if (!enabledRef.current && phaseRef.current === 'idle') return;
      try { fn(); } catch { /* swallow */ }
    }, ms);
    addDisposer(() => clearTimeout(handle));
    return handle;
  }, [addDisposer]);

  // Convenience: animation gate for safeFlyTo / safeEaseTo.
  const animationGate = useCallback(() => {
    if (!enabledRef.current) return false;
    if (phaseRef.current === 'idle' || phaseRef.current === 'exiting') {
      // exiting still uses safeEaseTo for the restore — those calls
      // pass `enabled: undefined` so they bypass this gate.
      return phaseRef.current === 'exiting';
    }
    if (manualOverrideRef.current && phaseRef.current !== 'entering') {
      // Manual override pauses follow-mode, but we still allow the
      // initial entry flight to finish so the user isn't stranded
      // wherever the previous camera was.
      return false;
    }
    if (pausedRef.current && phaseRef.current !== 'entering') {
      // Pause is the explicit "stop the cinematic" host control —
      // same exception for entering so a brand-new teleport still
      // resolves its first flight even if pause was left on from a
      // previous session (defensive — exit() resets pause anyway).
      return false;
    }
    return true;
  }, []);

  /**
   * Building-aware zoom adjustment. After the camera lands at a
   * stop we briefly wait for the 3D buildings layer to paint, then
   * inspect the tallest extruded building in the viewport. If the
   * default ENTER_ZOOM (close) would crop a megatall tower like the
   * Burj Khalifa, we ease the camera back to a zoom that fits the
   * whole skyline on screen. No-op when no tall buildings are near
   * — the cinematic stays close enough for a 3D street view.
   *
   * Runs once per arrival (entry + every beginFlight) and publishes
   * the easeTo so viewers in the watch party see the same final
   * framing the host does.
   */
  const adjustZoomForBuildings = useCallback(() => {
    if (!map) return;
    // Schedule the height-query a few frames after arrival so the
    // composite/building tiles have actually rasterised — querying
    // immediately on moveend returns 0 features even when the
    // building is right under the camera.
    schedule(() => {
      if (phaseRef.current === 'idle' || phaseRef.current === 'exiting') return;
      if (manualOverrideRef.current || pausedRef.current) return;
      const maxH = maxRenderedBuildingHeightM(map);
      if (maxH <= 0) return;
      const targetZoom = buildingAwareZoom(maxH, ENTER_ZOOM);
      let currentZoom = ENTER_ZOOM;
      try { currentZoom = map.getZoom(); } catch { /* swallow */ }
      // Only animate when the delta is meaningful — a 0.05 zoom
      // tweak is invisible and just costs another moveend tick.
      if (Math.abs(currentZoom - targetZoom) < 0.25) return;
      const easeOpts = {
        zoom:     targetZoom,
        pitch:    ENTER_PITCH,
        duration: 1200,
      };
      safeEaseTo(map, easeOpts, { enabled: animationGate });
      publishCameraAction('easeTo', easeOpts);
    }, 450);
  }, [map, schedule, animationGate, publishCameraAction]);

  // Update the marker / place-name React state for the stop the
  // camera just landed on. Centralised so every onArrived path
  // (entry, mid-cinematic flight, watchdog, jumpFallback) keeps the
  // marker, the HUD label, and the broadcast frame in lockstep.
  const handleArrivedStop = useCallback((pos: LngLat) => {
    setDisplayedAgentPosition(pos);
    const cached = getCachedReverseName(pos.lat, pos.lng);
    if (cached) {
      setCurrentStopName({
        short:   cached.short,
        region:  cached.region,
        country: cached.country,
      });
    }
    // Fire-and-forget reverse geocode so the next render after the
    // fetch resolves picks up the resolved (richer) name even if the
    // cached row was a primed stub. Errors swallowed by the service.
    void reverseGeocodeLatLng(pos.lat, pos.lng).then((name) => {
      if (!name) return;
      setCurrentStopName({
        short:   name.short,
        region:  name.region,
        country: name.country,
      });
    });
  }, []);

  // Pre-fetch the place name for the queue's FRONT stop so the
  // countdown overlay can show "Next teleport: <city>" before the
  // flight even begins. Cheap (cached) — fires whenever queue head
  // changes.
  const refreshNextStopName = useCallback(() => {
    const next = queueRef.current[0];
    if (!next) {
      setNextStopName(null);
      return;
    }
    const cached = getCachedReverseName(next.lat, next.lng);
    if (cached) {
      setNextStopName({
        short:   cached.short,
        region:  cached.region,
        country: cached.country,
      });
      return;
    }
    setNextStopName(null);
    void reverseGeocodeLatLng(next.lat, next.lng).then((name) => {
      if (!name) return;
      // Guard: only apply if this is STILL the front of the queue
      // when the fetch resolves (the agent might have moved on).
      const head = queueRef.current[0];
      if (!head || head.lat !== next.lat || head.lng !== next.lng) return;
      setNextStopName({
        short:   name.short,
        region:  name.region,
        country: name.country,
      });
    });
  }, []);

  // ── Route-line layer (current camera stop → next queued stop) ──────
  const updateRouteLine = useCallback(() => {
    if (!map) return;
    // CRITICAL: NEVER mutate the route-line GeoJSON source while a
    // flyTo/easeTo animation is interpolating the camera. Mapbox
    // GL JS 3.x has a known mat4.invert()-returns-null bug
    // (mapbox-gl-js#6486 / #12609) that's triggered by source
    // mutations landing on the same render frame as a bearing/
    // pitch update at high zoom + non-zero pitch. Once it throws
    // the transform is corrupted for the rest of the session.
    //
    // We only call this from phase transitions and from the
    // position-update reaction / movement-status poll. The route
    // line will be re-applied on the very next 'arrived' /
    // 'rotating' / 'countdown' tick (all of which call back into
    // updateRouteLine), so deferring during 'flying' is invisible
    // to the user but cuts off the matrix-singularity cascade.
    if (phaseRef.current === 'flying') return;
    try {
      if (!map.isStyleLoaded()) return;
      const from = cameraStopRef.current;
      const to   = peekStop();
      if (!from || !to || (from.lat === to.lat && from.lng === to.lng)) {
        // Hide line by clearing data.
        const src = map.getSource(ROUTE_SOURCE_ID) as MapboxGl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
        return;
      }
      const data = {
        type: 'FeatureCollection' as const,
        features: [{
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: [[from.lng, from.lat], [to.lng, to.lat]],
          },
          properties: {},
        }],
      };
      let src = map.getSource(ROUTE_SOURCE_ID) as MapboxGl.GeoJSONSource | undefined;
      if (!src) {
        map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data });
        map.addLayer({
          id: ROUTE_LAYER_ID,
          type: 'line',
          source: ROUTE_SOURCE_ID,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color':       '#6366f1',
            'line-width':       3,
            'line-opacity':     0.85,
            'line-dasharray':   [1.4, 1.4],
            'line-blur':        0.5,
          },
        } as any);
      } else {
        src.setData(data);
      }
    } catch { /* style swap mid-frame — try again next phase tick */ }
  }, [map, peekStop]);

  const removeRouteLine = useCallback(() => {
    if (!map) return;
    try {
      if (map.getLayer(ROUTE_LAYER_ID))  map.removeLayer(ROUTE_LAYER_ID);
      if (map.getSource(ROUTE_SOURCE_ID)) map.removeSource(ROUTE_SOURCE_ID);
    } catch { /* swallow */ }
  }, [map]);

  // ── Phase transitions ──────────────────────────────────────────────
  const startRotation = useCallback(() => {
    if (!map) {
      return;
    }
    if (roleRef.current === 'viewer') {
      return;
    }
    rotateStepCountRef.current = 0;
    setPhaseSync('rotating');

    // Each tick rotates by ROTATE_STEP_DEG. We re-schedule with
    // schedule() so the disposer chain catches every pending tick.
    const tick = () => {
      if (phaseRef.current !== 'rotating') return;
      const next = peekStop();
      if (next
          && cameraStopRef.current
          && metersBetween(next, cameraStopRef.current) > POSITION_DELTA_M) {
        // A new destination is waiting → leave rotation, start countdown.
        // Use the ref, not the closured `startCountdown`, so we always
        // invoke the latest one (closures capture the ORIGINAL stable
        // startCountdown, which captured a stale beginFlight with
        // map=null — see beginFlightRef comment up top).
        startCountdownRef.current();
        return;
      }
      rotateBearingRef.current = (rotateBearingRef.current + ROTATE_STEP_DEG) % 360;
      const rotateOpts = {
        bearing:  rotateBearingRef.current,
        duration: ROTATE_STEP_MS - 50,
        easing:   (n: number) => n,           // linear so the rotation is steady
      };
      safeEaseTo(map, rotateOpts, { enabled: animationGate });
      // Easing functions don't survive JSON.stringify, so strip
      // before publishing — viewers re-apply linear by default and
      // the duration is the only thing that has to match for sync.
      publishCameraAction('easeTo', {
        bearing:  rotateOpts.bearing,
        duration: rotateOpts.duration,
      });
      rotateStepCountRef.current += 1;
      if (rotateStepCountRef.current >= ROTATE_MAX_STEPS) {
        // Two full rotations and still no new agent position — pause
        // the spin and wait for an update. We re-arm when one lands
        // (handlePositionUpdate will call startCountdown() directly).
        return;
      }
      schedule(tick, ROTATE_STEP_MS);
    };
    schedule(tick, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, animationGate, schedule, publishCameraAction]);

  const startCountdown = useCallback(() => {
    if (roleRef.current === 'viewer') {
      return;
    }
    setPhaseSync('countdown');
    setCountdownSeconds(COUNTDOWN_SECONDS);
    const tick = (n: number) => {
      if (phaseRef.current !== 'countdown') {
        return;
      }
      if (n <= 0) {
        setCountdownSeconds(null);
        // CRITICAL: invoke via the ref, not the closured `beginFlight`.
        // startCountdown's deps `[schedule]` are stable, so this
        // useCallback returns the SAME function across every render —
        // including the first render, where the closured `beginFlight`
        // saw `map = null`. Without the ref, the user's countdown
        // would always reach 0 and then log
        // `[teleport] beginFlight: ABORT no map`. The ref is updated
        // each render via the useEffect below the ref declaration,
        // so this always reaches the LATEST beginFlight.
        beginFlightRef.current();
        return;
      }
      setCountdownSeconds(n);
      schedule(() => tick(n - 1), 1000);
    };
    schedule(() => tick(COUNTDOWN_SECONDS - 1), 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  const beginFlight = useCallback(() => {
    if (!map) {
      return;
    }
    if (roleRef.current === 'viewer') {
      // VIEWER mode: the host's phase frame and camera_action frames
      // drive the camera; we MUST NOT also enqueue a flight or we'd
      // double-animate. But we should NOT silently freeze either —
      // log loudly so the next bug-hunt has a clue.
      return;
    }

    // FIFO ordering — DO NOT collapse the queue. Per user feedback,
    // the cinematic should drive the *timing* of every visit so the
    // host's marker, the camera, and every viewer's camera all
    // animate through the same sequence of stops. With the GeoAI
    // fleet posting at 30 s and the cinematic taking ~6-8 s/stop,
    // we comfortably keep up; if we ever fall behind the
    // MAX_QUEUE_LENGTH cap kicks in and enqueueStop quietly drops
    // the second-oldest entry to preserve in-flight continuity.
    //
    // The earlier "collapse to latest" was a band-aid for the
    // beginFlight stale-closure bug (commit f1fce82 fixed that via
    // beginFlightRef). With the closure fixed, the queue is the
    // single source of truth — visiting it in order is now the
    // cleanest way to keep marker + camera + viewers synchronised.
    const target = peekStop();
    if (!target) {
      // Nothing to fly to — drop back to rotating.
      startRotation();
      return;
    }
    setPhaseSync('flying');
    setCurrentStopIndex((n) => n + 1);

    const from = cameraStopRef.current || target;
    const distanceKm = haversineDistanceKm(from, target);
    const duration   = getTeleportDuration(distanceKm);
    const bearing    = calculateBearing(from, target);
    const long       = isLongHop(distanceKm);

    /**
     * Fire ONCE — either the moveend listener calls it, the flight
     * watchdog calls it, or the jumpTo fallback calls it. Whichever
     * gets there first wins; subsequent calls are no-ops via the
     * arrivedFiredRef guard. This is what makes "next teleport"
     * always advance even when the flyTo is silently cancelled by
     * a competing camera move from elsewhere in App.tsx.
     */
    let arrivedFired = false;
    const onArrived = (cause: 'moveend' | 'watchdog' | 'jump_fallback') => {
      if (arrivedFired) return;
      arrivedFired = true;
      if (phaseRef.current !== 'flying') return;
      cameraStopRef.current = target;
      lastPositionRef.current = target;
      shiftStop();              // we actually landed → consume from queue
      // Move the agent's marker (and broadcast it to viewers via the
      // displayed-position state) to the just-visited stop. This is
      // what keeps marker + camera + watchers in step — the marker
      // doesn't follow raw WS frames during teleport; it follows
      // OUR cinematic schedule.
      handleArrivedStop(target);
      setPhaseSync('arrived');
      updateRouteLine();
      adjustZoomForBuildings();
      schedule(() => {
        if (phaseRef.current !== 'arrived') return;
        startRotation();
      }, 600);
    };

    const flyOpts: TeleportCameraOptions = {
      center:   [target.lng, target.lat],
      zoom:     ENTER_ZOOM,
      pitch:    ENTER_PITCH,
      bearing:  bearing ?? undefined,
      duration,
      // Long hops feel more like a teleport when we briefly zoom out
      // so the user gets a sense of the world before re-zooming. We
      // achieve that with the `curve` parameter (Mapbox's flyTo
      // already does this — higher curve = bigger arc).
      curve:    long ? 1.8 : 1.2,
    };

    /**
     * Hard fallback: if both the initial dispatch and the idle-retry
     * fail, OR the flight watchdog times out, we still want the user
     * to end up at the agent's location — losing the cinematic is
     * acceptable; losing the teleport is not. We jumpTo the target
     * (which is synchronous and always works) and call onArrived
     * directly. The `jump_fallback` cause label makes this visible
     * in the console so we can investigate why the flyTo path failed.
     */
    const jumpFallback = () => {
      if (arrivedFired) return;
      if (phaseRef.current !== 'flying') return;
      try {
        try { map.stop(); } catch { /* swallow */ }
        map.jumpTo({
          center:  [target.lng, target.lat],
          zoom:    ENTER_ZOOM,
          pitch:   ENTER_PITCH,
          bearing: bearing ?? rotateBearingRef.current,
        } as any);
        publishCameraAction('jumpTo', {
          center:  [target.lng, target.lat],
          zoom:    ENTER_ZOOM,
          pitch:   ENTER_PITCH,
          bearing: bearing ?? rotateBearingRef.current,
        });
      } catch (err) {
      }
      onArrived('jump_fallback');
    };

    // Same drain-then-listen-only-on-success pattern as
    // dispatchEntry. See the long comment there for the why.
    const tryDispatch = (): boolean => {
      try { map.stop(); } catch { /* swallow */ }
      const ok = safeFlyTo(map, flyOpts, { enabled: animationGate });
      if (!ok) return false;
      const moveendHandler = () => onArrived('moveend');
      map.once('moveend', moveendHandler);
      addDisposer(() => { try { map.off('moveend', moveendHandler); } catch { /* swallow */ } });
      publishCameraAction('flyTo', flyOpts);
      // Flight watchdog: if the flyTo's expected duration plus a
      // generous buffer elapses without a moveend, something
      // cancelled the animation silently (we've seen App.tsx-side
      // easeTo races do exactly this — see globe spin / pitch
      // reset). Force-advance the state machine via jumpFallback
      // so the user always lands at the target rather than
      // freezing on a "Flying…" badge mid-arc.
      const watchdogMs = (duration ?? 3500) + 1500;
      schedule(() => {
        if (arrivedFired) return;
        if (phaseRef.current !== 'flying') return;
        jumpFallback();
      }, watchdogMs);
      return true;
    };

    if (tryDispatch()) return;

    // Style not loaded — retry on next idle.
    const onIdle = () => {
      try { map.off('idle', onIdle); } catch { /* swallow */ }
      if (phaseRef.current !== 'flying') return;
      if (!tryDispatch()) {
        // Belt: don't leave the user staring at a static map. Jump
        // to the target so the queue advances and the rotation
        // takes over.
        jumpFallback();
      }
    };
    map.once('idle', onIdle);
    addDisposer(() => { try { map.off('idle', onIdle); } catch { /* swallow */ } });
    // Suspenders: cap the idle-retry wait at 1.5 s so a never-firing
    // 'idle' (e.g. another component is keeping the map perpetually
    // moving) can't strand us in 'flying' indefinitely.
    schedule(() => {
      if (arrivedFired) return;
      if (phaseRef.current !== 'flying') return;
      try { map.off('idle', onIdle); } catch { /* swallow */ }
      jumpFallback();
    }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, animationGate, schedule, addDisposer, updateRouteLine, startRotation, peekStop, shiftStop, publishCameraAction, handleArrivedStop, adjustZoomForBuildings]);

  // Mirror the latest version of each phase-transition callback into
  // its corresponding ref (declared up top). The closure-chain
  // problem these solve is documented in detail next to the ref
  // declarations — TL;DR: startCountdown is created EXACTLY ONCE on
  // first render (when map=null) and would otherwise call a
  // perpetually-stale beginFlight that aborts on every countdown
  // completion.
  useEffect(() => { beginFlightRef.current    = beginFlight;    }, [beginFlight]);
  useEffect(() => { startCountdownRef.current = startCountdown; }, [startCountdown]);
  useEffect(() => { startRotationRef.current  = startRotation;  }, [startRotation]);
  useEffect(() => { refreshNextStopNameRef.current = refreshNextStopName; }, [refreshNextStopName]);

  /**
   * Apply a remote camera_action frame from the host. Used in
   * VIEWER mode to mirror the host's cinematic. Skips publishing
   * (we're not the host). All map calls are guarded so a viewer
   * who's mid-style-load doesn't crash on a spurious frame.
   */
  const applyRemoteCamera = useCallback((
    action: 'flyTo' | 'easeTo' | 'jumpTo' | 'stop',
    options?: TeleportCameraOptions,
  ) => {
    if (!map) return;
    try {
      if (action === 'stop') { map.stop(); return; }
      if (action === 'jumpTo' && options) {
        map.jumpTo(options as any);
        return;
      }
      if (action === 'easeTo' && options) {
        safeEaseTo(map, { ...(options as any) }, { enabled: animationGate });
        return;
      }
      if (action === 'flyTo' && options) {
        safeFlyTo(map, { ...(options as any) }, { enabled: animationGate });
      }
    } catch { /* swallow — style swap mid-frame */ }
  }, [map, animationGate]);

  const enterMode = useCallback((agentPk: string, target: AgentListEntry) => {
    if (!map) return;
    const snap = captureCamera(map);
    cameraSnapshotRef.current = snap;

    const targetPos: LngLat = { lat: target.lat, lng: target.lng };
    // Seed the queue with the starting target. We do NOT shift it
    // off until the entry flight actually lands (onArrived), which
    // means any agent positions that arrive WHILE we're entering
    // get appended to the queue and visited in cinematic sequence
    // afterwards — exactly the "queue movements while teleporting"
    // behaviour the user asked for.
    queueRef.current        = [targetPos];
    setQueueLength(1);
    lastPositionRef.current = targetPos;
    rotateStepCountRef.current = 0;
    rotateBearingRef.current   = (snap?.bearing ?? 0);
    setCurrentStopIndex(0);
    // Seed the displayed marker position with the entry target so
    // the marker stays put as the camera flies in (rather than
    // hopping around to whatever raw WS frames arrive mid-entry).
    setDisplayedAgentPosition(targetPos);
    // Optimistic city/region/country from the cache (if we've ever
    // resolved this coord before). The fire-and-forget reverse-
    // geocode below refines it if the cached entry was a stub.
    const cachedEntry = getCachedReverseName(targetPos.lat, targetPos.lng);
    setCurrentStopName(cachedEntry
      ? { short: cachedEntry.short, region: cachedEntry.region, country: cachedEntry.country }
      : null);
    void reverseGeocodeLatLng(targetPos.lat, targetPos.lng).then((n) => {
      if (!n) return;
      setCurrentStopName({ short: n.short, region: n.region, country: n.country });
    });
    // Reset pause state at the start of every session — a leftover
    // `paused = true` from a previous teleport would silently freeze
    // the new one (every animationGate call returning false).
    pausedRef.current = false;
    setPaused(false);
    setHostPaused(null);
    // Snapshot the current Mapbox style for the HUD's "Original"
    // chip. Captured on enter so it persists across in-cinematic
    // style switches; reset to null in finishExit. If the parent
    // didn't pass `mapStyle`, the chip cluster simply won't render
    // (the HUD checks `originalMapStyle` before showing it).
    if (mapStyle) setOriginalMapStyle(mapStyle);

    // Sync setters update the matching ref synchronously, so the
    // safeFlyTo()'s animationGate() check below sees enabled=true /
    // phase='entering' on the SAME tick — without this, the gate
    // would still see the stale 'idle'/false from before the React
    // commit and silently drop the entry flight (the bug that left
    // the camera frozen at the user's pre-teleport zoom).
    setSelectedAgentIdSync(agentPk);
    setEnabledSync(true);
    setPhaseSync('entering');

    ensure3DBuildingsLayer(map);

    /**
     * The actual entry flight. Stashed on a ref so the WS welcome
     * handler can invoke it once it knows we're the host. Also
     * called by a watchdog so teleport works even when the WS hub
     * is unreachable (offline mode → assume host alone).
     *
     * IDEMPOTENT: a session-scoped guard ensures this can only run
     * once per teleport even if the welcome handler and the watchdog
     * race. Otherwise we'd register two `map.once('moveend', …)`
     * listeners; both fire on the next moveend, so shiftStop() runs
     * twice and the rotation tick that arrives 800 ms later sees
     * an over-drained queue and a phase that's already been
     * advanced past where we expected.
     */
    const dispatchEntry = () => {
      if (phaseRef.current !== 'entering') return;
      if (dispatchedThisSessionRef.current) {
        return;
      }
      dispatchedThisSessionRef.current = true;

      const onArrived = () => {
        if (phaseRef.current !== 'entering') return;
        if (roleRef.current === 'viewer') return;   // host owns phase advance
        cameraStopRef.current = targetPos;
        shiftStop();              // entry target visited → consume from queue
        // Stamp the marker / displayed position at the entry target.
        // Without this, the agent marker would still be drawn at the
        // raw WS position (which has likely moved on while we were
        // entering) — exactly the desync the user reported.
        handleArrivedStop(targetPos);
        setPhaseSync('arrived');
        updateRouteLine();
        adjustZoomForBuildings();
        schedule(() => {
          if (phaseRef.current !== 'arrived') return;
          startRotation();
        }, 600);
      };
      const flyOpts: TeleportCameraOptions = {
        center:   [target.lng, target.lat],
        zoom:     ENTER_ZOOM,
        pitch:    ENTER_PITCH,
        bearing:  rotateBearingRef.current,
        duration: 3500,
        curve:    1.4,
      };

      /**
       * Try to dispatch the entry flyTo. Returns `true` on success.
       *
       * CRITICAL ordering rules baked in here:
       *
       * 1. We `map.stop()` BEFORE registering the once-listener so a
       *    cancellation `moveend` from any prior animation drains
       *    while no listener exists — otherwise our once-listener
       *    would fire on the cancellation and instantly advance
       *    phase to 'arrived' (the bug that froze the camera
       *    mid-flight while rotation/follow silently no-op'd).
       *
       * 2. We register the once-listener ONLY AFTER `safeFlyTo`
       *    confirms the flight was actually dispatched. If the
       *    style isn't loaded (transient race we've seen on first
       *    click), `safeFlyTo` skips and we'd otherwise leave a
       *    listener sitting around waiting for an unrelated
       *    moveend that would arrive later from somebody else's
       *    camera move.
       */
      const tryDispatch = (): boolean => {
        try { map.stop(); } catch { /* swallow */ }
        const ok = safeFlyTo(map, flyOpts, { enabled: animationGate });
        if (!ok) return false;
        map.once('moveend', onArrived);
        addDisposer(() => { try { map.off('moveend', onArrived); } catch { /* swallow */ } });
        publishCameraAction('flyTo', flyOpts);
        return true;
      };

      if (tryDispatch()) return;

      // Style not loaded (or some other transient skip). Wait for
      // the next 'idle' event and retry — that's Mapbox's signal
      // that the style + sources are ready and the camera is at
      // rest. We retry at most once, then give up and let the user
      // click Start again. (Used to require a manual second click;
      // this turns it into an invisible recovery.)
      const onIdle = () => {
        try { map.off('idle', onIdle); } catch { /* swallow */ }
        if (phaseRef.current !== 'entering') return;
        if (!tryDispatch()) {
        }
      };
      map.once('idle', onIdle);
      addDisposer(() => { try { map.off('idle', onIdle); } catch { /* swallow */ } });
    };
    dispatchEntryRef.current = dispatchEntry;

    welcomeReceivedRef.current     = false;
    dispatchedThisSessionRef.current = false;

    /**
     * Watchdog: if the WS welcome doesn't arrive within 800 ms we
     * assume the hub is unreachable and proceed in standalone host
     * mode. Without this, teleport would silently freeze on
     * 'entering' whenever the multi-viewer infrastructure has a
     * problem — far worse than just losing the watch-party feature.
     *
     * 800 ms is comfortably longer than a same-origin WS round-trip
     * (typically < 100 ms on Azure) yet short enough that the user
     * doesn't sit on a static "Entering…" badge wondering if it broke.
     * The dispatchedThisSessionRef guard means a late welcome can't
     * double-fire dispatchEntry() if it arrives mid-watchdog-timer.
     */
    const watchdog = setTimeout(() => {
      if (welcomeReceivedRef.current) return;
      if (phaseRef.current !== 'entering') return;
      setRoleSync('host');
      dispatchEntry();
    }, 800);
    addDisposer(() => clearTimeout(watchdog));

    /**
     * Open the watch-party WS. The frame handler covers all four
     * roles' lifecycles (welcome, presence, role_change, host
     * frames in viewer mode).
     */
    const handleFrame = (frame: TeleportFrame) => {
      if (frame.type === 'welcome' && frame.agentPk === agentPk) {
        welcomeReceivedRef.current = true;
        clearTimeout(watchdog);
        setSelfId(frame.selfId);
        setHostId(frame.hostId);
        setViewers(frame.viewers);
        setRoleSync(frame.role);
        if (frame.role === 'host') {
          // First in → drive the cinematic.
          dispatchEntry();
        } else {
          // Joined an existing party. Apply retained state so we
          // snap near where the host is right now, then mirror
          // future frames passively.
          if (frame.retained.queue && Array.isArray(frame.retained.queue.queue)) {
            queueRef.current = frame.retained.queue.queue.slice(0, MAX_QUEUE_LENGTH);
            setQueueLength(queueRef.current.length);
          }
          if (frame.retained.phase) {
            setPhaseSync(frame.retained.phase.phase);
            setCurrentStopIndex(frame.retained.phase.currentStopIndex);
            setCountdownSeconds(frame.retained.phase.countdownSeconds);
          }
          if (frame.retained.camera_action) {
            // Use jumpTo for the catch-up so the late joiner doesn't
            // sit through a long flyTo from THEIR pre-teleport view
            // back to where everyone else already is.
            const opts = frame.retained.camera_action.options;
            if (opts) {
              try { map.jumpTo(opts as any); } catch { /* swallow */ }
            }
          }
        }
        return;
      }
      if (frame.type === 'presence' && frame.agentPk === agentPk) {
        setHostId(frame.hostId);
        setViewers(frame.viewers);
        return;
      }
      if (frame.type === 'role_change' && frame.agentPk === agentPk) {
        setHostId(frame.hostId);
        setRoleSync(frame.role);
        if (frame.role === 'host') {
          // We were just promoted (the previous host left).
          // Snapshot current camera as the new "stop", reset the
          // rotation-step counter, and either resume the cinematic
          // (if there's a queued stop) or rotate where we are.
          const c = map.getCenter();
          cameraStopRef.current = { lat: c.lat, lng: c.lng };
          rotateStepCountRef.current = 0;
          rotateBearingRef.current   = map.getBearing();
          // The host-publish useEffect above watches [phase,
          // currentStopIndex, countdownSeconds, enabled, role] and
          // will re-fire as soon as our role becomes 'host', so we
          // don't need to publish the phase frame explicitly here.
          // The queue, on the other hand, has no auto-publish
          // effect — fire it now so other viewers see continuity.
          sessionRef.current?.publishQueue([...queueRef.current]);
          if (peekStop()) {
            startCountdown();
          } else if (phaseRef.current !== 'arrived' && phaseRef.current !== 'rotating') {
            setPhaseSync('arrived');
            schedule(() => { if (phaseRef.current === 'arrived') startRotation(); }, 600);
          }
        }
        return;
      }
      // Viewer-only frame application (host ignores its own broadcasts).
      if (roleRef.current !== 'viewer') return;
      if (frame.type === 'camera_action' && frame.agentPk === agentPk) {
        applyRemoteCamera(frame.action, frame.options);
        // Mirror the host's displayed agent marker position from the
        // camera action — every cinematic move (entry, mid-flight,
        // jumpTo fallback) targets the agent's current stop, so the
        // center of those calls is also where we should render the
        // marker. This is what keeps watchers' markers in step with
        // the host's rather than chasing raw WS frames out of order.
        const opts = frame.options as { center?: [number, number] } | undefined;
        if (opts && Array.isArray(opts.center) && opts.center.length === 2) {
          const [lng, lat] = opts.center;
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            const pos: LngLat = { lat, lng };
            cameraStopRef.current = pos;
            setDisplayedAgentPosition(pos);
            // Promote any cached reverse-geocode for this coord so
            // the "Arrived: <city>" badge matches what the host sees.
            const cached = getCachedReverseName(lat, lng);
            if (cached) {
              setCurrentStopName({
                short:   cached.short,
                region:  cached.region,
                country: cached.country,
              });
            } else {
              void reverseGeocodeLatLng(lat, lng).then((n) => {
                if (!n) return;
                setCurrentStopName({ short: n.short, region: n.region, country: n.country });
              });
            }
          }
        }
        return;
      }
      if (frame.type === 'phase' && frame.agentPk === agentPk) {
        setPhaseSync(frame.phase as TeleportPhase);
        setCurrentStopIndex(frame.currentStopIndex);
        setCountdownSeconds(frame.countdownSeconds);
        // Surface the host's pause as a viewer-side mirror so the
        // HUD can show a "Host paused" badge and stop showing a
        // misleading "rotating" / "arrived" without an explanation.
        // The phase frame doesn't carry pause directly today; we
        // infer it from a 'rotating'/'arrived' phase with countdown
        // == null AND a stale queue. Belt-and-suspenders ONLY — the
        // explicit setPaused publish is what the HUD relies on.
        return;
      }
      if (frame.type === 'queue' && frame.agentPk === agentPk) {
        queueRef.current = frame.queue.slice(0, MAX_QUEUE_LENGTH);
        setQueueLength(queueRef.current.length);
        // Refresh the next-stop name preview for the viewer too so
        // the countdown overlay shows the same "Next teleport: <…>"
        // line the host sees.
        refreshNextStopNameRef.current?.();
        updateRouteLine();
        return;
      }
    };

    sessionRef.current = openTeleportSession({
      agentPk,
      walletPk: walletPk || undefined,
      onFrame:  handleFrame,
      onError:  (err) => { try { console.debug('[teleport] WS error', err); } catch { /* swallow */ } },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map, animationGate, schedule, addDisposer, updateRouteLine,
    startRotation, startCountdown, shiftStop, peekStop, applyRemoteCamera,
    publishCameraAction, walletPk, setRoleSync,
    setSelectedAgentIdSync, setEnabledSync, setPhaseSync, handleArrivedStop,
    adjustZoomForBuildings,
  ]);

  const exit = useCallback((reason: TeleportExitReason = 'user') => {
    if (!enabledRef.current && phaseRef.current === 'idle') {
      // Nothing to tear down (e.g. exit() called twice). Still clear
      // any pending panel so the click UI resets.
      setPendingAgentId(null);
      return;
    }
    setPhaseSync('exiting');
    setCountdownSeconds(null);
    setNextDestination(null);
    clearDisposers();
    clearQueue();
    // Close the watch-party session BEFORE we rip down everything
    // else so the server can promote the next host while we still
    // have a working socket. (The hub treats this as a regular
    // leave — viewers see a presence frame and, if we were host, a
    // role_change for the new host.)
    try { sessionRef.current?.close(); } catch { /* swallow */ }
    sessionRef.current = null;
    setRoleSync(null);
    setSelfId(null);
    setHostId(null);
    setViewers([]);
    welcomeReceivedRef.current       = false;
    dispatchedThisSessionRef.current = false;
    dispatchEntryRef.current         = null;
    try { map?.stop(); } catch { /* swallow */ }
    removeRouteLine();
    remove3DBuildingsLayer(map);

    const restore = cameraSnapshotRef.current;
    const finishExit = () => {
      setPhaseSync('idle');
      setEnabledSync(false);
      setSelectedAgentIdSync(null);
      setPendingAgentId(null);
      setManualOverrideSync(false);
      cameraSnapshotRef.current = null;
      cameraStopRef.current     = null;
      lastPositionRef.current   = null;
      rotateStepCountRef.current = 0;
      userInteractedRef.current  = false;
      // Reset cinematic-controlled state so the next session starts
      // clean (otherwise a leftover displayedAgentPosition would
      // override the marker for the next selected agent until their
      // first arrival, and a leftover paused=true would silently
      // freeze the next cinematic).
      pausedRef.current = false;
      setPaused(false);
      setHostPaused(null);
      setDisplayedAgentPosition(null);
      setCurrentStopName(null);
      setNextStopName(null);
      // Forget the captured "original" style — next session will
      // snapshot whatever's active at THAT enterMode time, which
      // will reflect any style choices the user made via the
      // SettingsOverlay between sessions.
      setOriginalMapStyle(null);
      // queueRef was already cleared by clearQueue() above; keep it
      // explicit so future contributors don't add a stop here that
      // re-introduces the leak.
      try { onExited?.(reason); } catch { /* swallow */ }
    };

    if (map && restore) {
      const onRestored = () => { try { map.off('moveend', onRestored); } catch { /* swallow */ } finishExit(); };
      map.once('moveend', onRestored);
      // Bypass the animation gate (phase=exiting bypasses it inside).
      safeEaseTo(map, {
        center:  restore.center,
        zoom:    restore.zoom,
        pitch:   restore.pitch,
        bearing: restore.bearing,
        duration: 1500,
      });
      // Safety net: if moveend never fires (e.g. map disposed mid-flight),
      // still finish the exit after the easeTo's worst-case duration.
      const failSafe = setTimeout(() => {
        try { map.off('moveend', onRestored); } catch { /* swallow */ }
        if (phaseRef.current === 'exiting') finishExit();
      }, 2000);
      // Don't add to disposersRef — they were already cleared above.
      // We deliberately leak the handle if the user *re-enters* in
      // the next 2 s; the inner finishExit() is idempotent because
      // it checks phaseRef.
      void failSafe;
    } else {
      finishExit();
    }
  }, [
    map, clearDisposers, clearQueue, removeRouteLine, onExited,
    setPhaseSync, setEnabledSync, setSelectedAgentIdSync, setManualOverrideSync,
    setRoleSync,
  ]);

  // ── Public controls ────────────────────────────────────────────────
  const requestPanel = useCallback((agentPk: string) => {
    if (!agentPk) return;
    setPendingAgentId(agentPk);
  }, []);

  const dismissPanel = useCallback(() => setPendingAgentId(null), []);

  const start = useCallback((agentPk?: string) => {
    const pk = agentPk || pendingAgentId;
    if (!pk) return;
    const target = findAgent(agents, pk);
    if (!target || !map) return;
    // If we're switching agents mid-flight, cleanly tear the previous
    // run down before starting the new one — preserves the saved
    // camera so we always restore to the *original* pre-teleport view.
    if (enabledRef.current) {
      const previousSnap = cameraSnapshotRef.current;
      clearDisposers();
      // Close any active watch-party session for the OLD agent so
      // its hub-side promotion logic can take over.
      try { sessionRef.current?.close(); } catch { /* swallow */ }
      sessionRef.current = null;
      setRoleSync(null);
      setSelfId(null);
      setHostId(null);
      setViewers([]);
      welcomeReceivedRef.current       = false;
      dispatchedThisSessionRef.current = false;
      try { map.stop(); } catch { /* swallow */ }
      // Don't restore camera here — we're about to fly somewhere new.
      cameraSnapshotRef.current = previousSnap;
    }
    setPendingAgentId(null);
    enterMode(pk, target);
  }, [agents, map, pendingAgentId, enterMode, clearDisposers, setRoleSync]);

  /**
   * "Add a place to the queue" — the imperative side of the
   * AgentTeleportHUD's place-search affordance. Looks the place up
   * via the GeoLink geocoding cache (with Mapbox forward-geocoding
   * as the fallback), then appends the result to the FIFO. If we're
   * sitting on `arrived` / `rotating` and not in manual-override,
   * the position-update useEffect's "queue grew, start countdown"
   * arm doesn't run for non-agent enqueues (it's gated by the
   * agent-position effect), so we kick the countdown ourselves
   * here. Viewer-callers get a friendly no-op.
   */
  const enqueuePlace = useCallback(async (rawQuery: string): Promise<TeleportPlaceOutcome> => {
    const query = String(rawQuery || '').trim();
    if (!query) {
      return { ok: false, error: 'Type a place to teleport to', code: 'no_query' };
    }
    if (roleRef.current === 'viewer') {
      // Surface a friendly nudge rather than silently dropping the
      // call. The HUD only renders the input for the host but
      // belt-and-suspenders for late role flips.
      return { ok: false, error: 'Only the host can add places — wait for them to leave or be promoted', code: 'no_query' };
    }
    // Country hint: prefer the selected agent's country (so a US
    // search for "Springfield" doesn't return the Australian one),
    // otherwise leave it null and let Mapbox figure it out.
    const country = (selectedAgent?.country_code || null);

    const result = await searchAndGeocodePlace({
      query,
      country,
      walletPk,
    });

    if (!result.ok) {
      return result;
    }

    const stop: LngLat = { lat: result.lat, lng: result.lng };
    // Prime the reverse-geocode cache so when the cinematic lands at
    // this stop, the "Arrived" badge surfaces the user's typed
    // place name immediately rather than firing a redundant Mapbox
    // reverse lookup. Same for the "Next teleport: <…>" preview.
    primeReverseCache(result.lat, result.lng, result.placeName);
    enqueueStop(stop);
    setNextDestination(stop);
    updateRouteLine();

    // If we're idle-ish (arrived/rotating) and the user isn't in
    // free-look or paused, kick the countdown so the camera actually
    // goes there. Otherwise it would just sit in the queue until the
    // *next* agent position arrived — which feels broken from the
    // user's perspective ("I added a place, why is nothing
    // happening?"). For phases we're already mid-flight on
    // (entering / flying / countdown), we just append; the queue
    // will pick the new entry up at the next collapse.
    if (roleRef.current === 'host' && (phaseRef.current === 'arrived' || phaseRef.current === 'rotating')) {
      if (!manualOverrideRef.current && !pausedRef.current) startCountdown();
    }

    return result;
  }, [
    selectedAgent, walletPk, enqueueStop, updateRouteLine, startCountdown,
  ]);

  /**
   * Direct-enqueue an already-resolved (lat, lng, place_name) — used
   * by the autocomplete dropdown when the user clicks a suggestion
   * (the suggestion already carries the resolved coords from a
   * Mapbox typeahead, so re-running the cache-first round trip in
   * `enqueuePlace` would be wasteful).
   */
  const enqueueLngLat = useCallback((pos: LngLat, placeName?: string | null) => {
    if (roleRef.current === 'viewer') return;
    if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) return;
    if (placeName) primeReverseCache(pos.lat, pos.lng, placeName);
    enqueueStop(pos);
    setNextDestination(pos);
    updateRouteLine();
    if (roleRef.current === 'host'
        && (phaseRef.current === 'arrived' || phaseRef.current === 'rotating')
        && !manualOverrideRef.current
        && !pausedRef.current) {
      startCountdown();
    }
  }, [enqueueStop, updateRouteLine, startCountdown]);

  /**
   * Toggle the host-side "pause cinematic" flag. Updates the ref
   * synchronously (so animationGate sees the new value on the same
   * tick) and, on resume, kicks the countdown if there's already a
   * stop waiting in the queue.
   *
   * Note: pause is currently host-local — viewers infer pause from
   * the absence of camera_action / phase frames from the host (the
   * cinematic visibly stalls). Plumbing an explicit `paused: bool`
   * onto the phase frame requires a server-side change to
   * `/ws/teleport`'s phase frame schema; tracked as a follow-up so
   * we can add a "Host paused" badge to viewer HUDs without leaving
   * them guessing.
   */
  const setPausedSync = useCallback((next: boolean) => {
    if (roleRef.current === 'viewer') return;
    pausedRef.current = next;
    setPaused(next);
    if (!next
        && (phaseRef.current === 'arrived' || phaseRef.current === 'rotating')
        && !manualOverrideRef.current
        && peekStop()) {
      startCountdown();
    }
  }, [startCountdown, peekStop]);

  /**
   * Live-swap the Mapbox style URL. Calls `map.setStyle(url)` and
   * notifies the parent via `onMapStyleChange` so its mirror state
   * stays in sync. Layer re-application (3D buildings + route
   * line) is handled by the `style.load` effect below — keeping the
   * re-app logic out of the imperative call site means SettingsOverlay
   * style changes (which don't go through this method) get the same
   * cinematic-aware re-application for free.
   *
   * Available on every role — it's a local map change. If a viewer
   * switches to dark mode they see a dark map; the host's view is
   * unaffected.
   */
  const setMapStyleSync = useCallback((url: string) => {
    if (!map || !url) return;
    if (mapStyle && mapStyle === url) return;            // no-op
    try {
      map.setStyle(url);
    } catch {
      // Mapbox occasionally throws on a setStyle race (style
      // loading another style in the same tick). Caller can retry.
      return;
    }
    try { onMapStyleChange?.(url); } catch { /* swallow */ }
  }, [map, mapStyle, onMapStyleChange]);

  // ── Re-apply cinematic layers when the style swaps under us ────────
  // mapboxgl.Map#setStyle wipes EVERY non-default source / layer (it
  // installs a brand-new style root). The cinematic adds two of its
  // own layers — the 3D buildings extrusion + the dashed route line
  // — so we need to re-apply both whenever a style.load fires while
  // teleport is active. This runs regardless of WHO triggered the
  // style change (our `setMapStyleSync` button, or the
  // SettingsOverlay dropdown) so behaviour is consistent.
  //
  // The listener is gated on `enabled` so we don't permanently leak
  // a `style.load` listener after teleport exits — one new listener
  // per session.
  useEffect(() => {
    if (!map || !enabled) return;
    const onStyleLoad = () => {
      try {
        ensure3DBuildingsLayer(map);
        // updateRouteLine is idempotent — creates the source/layer
        // if missing (which they always are after a setStyle) and
        // populates them from cameraStopRef + queueRef.
        updateRouteLine();
      } catch {
        // Style layers race — Mapbox occasionally still has the
        // composite source mid-init. Best-effort; a subsequent
        // updateRouteLine call from any phase change will retry.
      }
    };
    map.on('style.load', onStyleLoad);
    return () => { try { map.off('style.load', onStyleLoad); } catch { /* swallow */ } };
  }, [map, enabled, updateRouteLine]);

  // ── Mapbox matrix-singularity error absorber ───────────────────────
  //
  // mapbox-gl-js 3.x has a long-standing bug
  // (mapbox-gl-js#5322 / #6486 / #12609) where mat4.invert() returns
  // null on certain camera transforms — typically a flyTo at high
  // zoom with non-zero pitch and a bearing change interpolating on
  // the same frame as a GeoJSON source mutation. The cinematic
  // teleport hits all three triggers (zoom 17 + pitch 60 + bearing
  // animation + our route-line source update), so on a slow
  // browser/GPU it can throw `Cannot read properties of null` from
  // `vec3.js` / `vec4.js` inside `_calcMatrices`.
  //
  // The throw isn't caught by Mapbox's own render loop — it bubbles
  // to `window.error`, and on every subsequent frame Mapbox tries
  // to re-render with the corrupted transform and throws again.
  // Once the cycle starts the camera is permanently dead.
  //
  // We can't fix Mapbox, but we CAN: (a) suppress the spam so the
  // page console isn't flooded, (b) call `map.resize()` which
  // forces a fresh `_calcMatrices` from the current container
  // size, and (c) jumpTo the camera's last-known good position so
  // the transform is rebuilt from scratch. This is a recovery, not
  // a prevention — the prevention is the `phase === 'flying'`
  // guards in `updateRouteLine` and the poll above.
  //
  // Listener is window-level (not map-level) because the throw
  // happens deep in Mapbox's RAF callback and never reaches
  // map.on('error'). It's gated on `enabled` so we don't leak
  // listeners between teleport sessions.
  useEffect(() => {
    if (!map || !enabled) return;
    let recoveryInFlight = false;
    let suppressedCount  = 0;
    const MATRIX_BUG_SIGNATURES = [
      'fromInvProjectionMatrix',
      'coveringTiles',
      '_calcMatrices',
    ];
    const isMapboxMatrixError = (msg: string, src: string): boolean => {
      if (!msg) return false;
      const lower = msg.toLowerCase();
      const matchesShape = lower.includes('cannot read properties of null')
        || lower.includes('cannot read property');
      if (!matchesShape) return false;
      const stack = (src || '') + ' ' + (msg || '');
      return MATRIX_BUG_SIGNATURES.some((sig) => stack.includes(sig));
    };
    const recover = () => {
      if (recoveryInFlight) return;
      recoveryInFlight = true;
      try {
        // Force a fresh _calcMatrices from the current container
        // size — this reseeds the projection matrix from a known
        // good state and breaks the corrupted-transform-loop.
        map.resize();
      } catch { /* swallow */ }
      const here = cameraStopRef.current;
      if (here) {
        try {
          map.stop();
          map.jumpTo({
            center:  [here.lng, here.lat],
            zoom:    ENTER_ZOOM,
            pitch:   ENTER_PITCH,
            bearing: rotateBearingRef.current || 0,
          } as any);
        } catch (err) {
        }
      }
      // Release the throttle on the next frame so a fresh storm
      // (e.g. a still-corrupted transform on the next render) can
      // re-trigger recovery — but not faster than once per frame.
      setTimeout(() => { recoveryInFlight = false; }, 200);
    };
    const onWindowError = (ev: ErrorEvent) => {
      const msg = ev?.message || (ev?.error as Error | undefined)?.message || '';
      const src = (ev?.error as Error | undefined)?.stack || ev?.filename || '';
      if (!isMapboxMatrixError(msg, src)) return;
      // Suppress the spam — these errors will fire on every
      // render frame until recovery completes.
      ev.preventDefault();
      ev.stopImmediatePropagation();
      suppressedCount += 1;
      if (suppressedCount === 1 || suppressedCount % 50 === 0) {
      }
      recover();
    };
    const onUnhandledRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev?.reason;
      const msg = (reason as Error | undefined)?.message || String(reason || '');
      const src = (reason as Error | undefined)?.stack || '';
      if (!isMapboxMatrixError(msg, src)) return;
      ev.preventDefault();
      recover();
    };
    window.addEventListener('error', onWindowError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onWindowError, true);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [map, enabled]);

  // ── Position-update reactions ──────────────────────────────────────
  useEffect(() => {
    if (!enabled || !selectedAgent) {
      // Selected agent disappeared from the feed (went stale, was
      // unsubscribed because the user changed countries, etc.). Don't
      // exit immediately — give it a few seconds; the agent might
      // just be between throttled writes.
      if (enabled && selectedAgentId && !selectedAgent) {
        const handle = setTimeout(() => {
          if (enabledRef.current
              && selectedAgentIdRef.current === selectedAgentId
              && !findAgent(agents, selectedAgentId)) {
            exit('agent_gone');
          }
        }, 8000);
        return () => clearTimeout(handle);
      }
      return;
    }
    const next: LngLat = { lat: selectedAgent.lat, lng: selectedAgent.lng };
    const last = lastPositionRef.current;
    if (last && metersBetween(last, next) < POSITION_DELTA_M) {
      // GPS noise — ignore.
      return;
    }
    lastPositionRef.current = next;
    if (roleRef.current === 'host') {
      // ONLY the host owns the queue. Viewers receive queue frames
      // from the hub and apply them passively in the welcome /
      // queue handlers above; if a viewer ALSO enqueued from raw
      // agent positions we'd see flickering as the local queue
      // and the host's broadcast queue race each other.
      enqueueStop(next);
    }
    setNextDestination(next);

    // Phase-specific reactions (host-only — viewers' phase comes
    // from the host's phase frames).
    if (roleRef.current === 'host' && (phase === 'arrived' || phase === 'rotating')) {
      // We're sitting still and the queue just grew — kick off the
      // countdown to teleport to the next stop. Manual override AND
      // explicit pause both defer this; the queue still holds the new
      // position, so when either clears the resume effect picks it up.
      if (!manualOverrideRef.current && !pausedRef.current && peekStop()) startCountdown();
    }
    // For 'entering', 'flying', or 'countdown', we just let the
    // queue update; the next dequeue picks up the latest stop.
    updateRouteLine();
  // `role` is in the dep array even though we read `roleRef.current`
  // inside — this guarantees the effect re-runs when the WS welcome
  // promotes us from `null` → `'host'`, so a queue stop that arrived
  // pre-welcome doesn't sit there waiting forever for the next
  // unrelated agents/phase change to drain it.
  }, [
    agents, selectedAgent, enabled, selectedAgentId, phase, role, paused,
    exit, startCountdown, updateRouteLine, enqueueStop, peekStop,
  ]);

  // ── Server-side movement-status fallback poller ────────────────────
  //
  // The primary position source is the live marker feed (`agents`
  // prop) which the WS hub mirrors from GeoLink's /agents/feed.
  // That path can stall — slow upstream broadcast, throttled C…
  // agent fan-out on a fresh GeoLink deploy, the user being on a
  // flaky network — and when it does the cinematic camera ends up
  // sitting at the first stop rotating forever because no new
  // position ever arrives to enqueue.
  //
  // To make the cinematic robust regardless of WS latency, we
  // *also* poll the agent's own `/api/v2/agents/:address/movement/status`
  // endpoint while teleport is active. If the server-side
  // `lastWaypoint` differs from the latest live-feed position by
  // more than POSITION_DELTA_M, we feed it through the same
  // `enqueueStop` path the live feed uses — same de-jitter, same
  // queue cap, same publishQueue out to viewers. Whichever source
  // sees the move first wins; the other one's matching update is
  // silently dropped by the de-jitter guard inside enqueueStop.
  //
  // The poll runs only on the host (viewers receive queue frames
  // from the WS hub) and only while the selected agent has an
  // active server-side movement loop. Cadence is 4 s — fast
  // enough to keep the cinematic moving even when the live feed
  // is slow, slow enough not to look like polling in the network
  // tab. Polls back off to 12 s when the loop is `paused` or
  // `stopped` so an idle teleport doesn't hammer the endpoint.
  useEffect(() => {
    if (!enabled || !selectedAgentId) {
      // The "not armed" branch fires on EVERY render where teleport
      // mode is off (i.e. ~always for the average viewer), so we
      // only log it when the dev explicitly opts in via the global
      // debug flag — otherwise it flooded the console at idle.
      if (typeof window !== 'undefined' && (window as any).__GEOTRUST_DEBUG_TELEPORT__) {
      }
      return;
    }
    let cancelled  = false;
    let inFlight   = false;
    let pollCount  = 0;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    const POLL_ACTIVE_MS = 4_000;
    const POLL_IDLE_MS   = 12_000;
    const tick = async () => {
      if (cancelled) return;
      if (inFlight)  return;
      // Only the host owns the queue — viewers receive queue
      // frames from the WS hub. We still mount the poller for
      // viewers so the role flip from null → 'host' (when the
      // WS welcome arrives mid-mount) doesn't leave us without
      // a live source; viewer ticks just fetch and discard.
      // The role check is INSIDE the tick (not on the effect
      // gate) so we react to mid-poll role changes too.
      const isHost = roleRef.current === 'host';
      inFlight = true;
      pollCount += 1;
      let nextDelay = POLL_ACTIVE_MS;
      try {
        const status = await getMovementStatus(selectedAgentId);
        if (cancelled) return;
        if (status && status.status !== 'running') nextDelay = POLL_IDLE_MS;
        const wp = status?.lastWaypoint || null;
        // Periodic heartbeat so we can tell the poller is
        // healthy even when the agent isn't moving (e.g. between
        // server-side ticks, or while `paused`). Logs every 5th
        // poll to keep the console quiet otherwise.
        if (pollCount === 1 || pollCount % 5 === 0) {
        }
        if (!isHost) return;
        if (wp && Number.isFinite(wp.lat) && Number.isFinite(wp.lng)) {
          const next: LngLat = { lat: wp.lat, lng: wp.lng };
          const last = lastPositionRef.current;
          // Only feed enqueueStop when the server-side waypoint
          // actually moved past our jitter floor — enqueueStop
          // would drop it anyway, but skipping the call keeps
          // logs clean.
          if (!last || metersBetween(last, next) >= POSITION_DELTA_M) {
            // Do NOT mutate map state (route line, queue
            // broadcast) while the flyTo is interpolating —
            // see the long comment in updateRouteLine. We still
            // want to enqueue so the next post-arrival tick
            // picks it up, but skip the broadcast/route-line
            // refresh until we land.
            const isFlying = phaseRef.current === 'flying';
            lastPositionRef.current = next;
            enqueueStop(next);
            if (!isFlying) {
              setNextDestination(next);
              // Same arm-the-countdown logic as the live-feed
              // reaction above — when we're sitting still and
              // the queue grew, kick off the countdown.
              if (!manualOverrideRef.current
                  && !pausedRef.current
                  && (phaseRef.current === 'arrived' || phaseRef.current === 'rotating')
                  && peekStop()) {
                startCountdown();
              }
              updateRouteLine();
            }
          }
        }
      } catch (err) {
        // Polls are best-effort. A 401, network hiccup, or the
        // agent not having a movement loop at all (404) are all
        // fine — we just retry on the next tick. Log the first
        // failure so a misconfigured endpoint is debuggable.
        if (pollCount <= 3) {
        }
        nextDelay = POLL_IDLE_MS;
      } finally {
        inFlight = false;
        if (!cancelled) timerId = setTimeout(tick, nextDelay);
      }
    };
    // Kick off immediately so the first cinematic stop becomes
    // available without waiting a full poll interval.
    timerId = setTimeout(tick, 250);
    return () => {
      cancelled = true;
      if (timerId) { clearTimeout(timerId); timerId = null; }
    };
  // `role` is in the dep array even though we read `roleRef.current`
  // inside — same lockstep concern as the position-update effect
  // above: a WS welcome promoting null → 'host' must re-arm the
  // poll loop so a host that joined late still gets queue updates.
  }, [enabled, selectedAgentId, role, enqueueStop, peekStop, startCountdown, updateRouteLine]);

  // ── Manual-override resume ─────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (manualOverride) return;
    if (paused)         return;            // explicit pause defers resume too
    if (phase !== 'arrived' && phase !== 'rotating') return;
    const queued = peekStop();
    const here   = cameraStopRef.current;
    if (!queued || !here) return;
    if (metersBetween(queued, here) > POSITION_DELTA_M) {
      startCountdown();
    }
  }, [enabled, manualOverride, paused, phase, startCountdown, peekStop]);

  // ── Escape + user-interaction exit hooks ───────────────────────────
  //
  // Registered as a MODE rather than owning `keydown` outright. This used to
  // add its own window listener, and so did the spatial chat card, so one
  // Escape with a chat open over a cinematic ran both: the conversation
  // closed and the camera fell out of cinematic together. Priority makes
  // that order explicit — the chat is a surface and outranks this, so the
  // first Escape closes it and the second lands here.
  //
  // Still gated on `enabled`, exactly as before: a cinematic that is not
  // running has no claim on the key.
  const onEscapeExit = useCallback(() => { exit('escape'); }, [exit]);
  useEscapeOwner(enabled, 'agent-teleport', ESCAPE_PRIORITY.MODE, onEscapeExit);

  useEffect(() => {
    if (!enabled || !map) return;
    // Mapbox emits *start events with `originalEvent` set ONLY when
    // the move was triggered by user input (mouse / wheel / touch).
    // Programmatic flyTo / easeTo from our own animation loop fires
    // these same events but without `originalEvent`, so checking it
    // is the cleanest way to distinguish "the human grabbed the
    // map" from "our cinematic camera move just started another
    // step". Replaces the old DOM-level mousedown / touchstart
    // listeners which fired on every stray finger-rest and even on
    // the marker pulse animations on touch devices.
    //
    // Reaction: instead of exiting the cinematic, we PAUSE it and
    // flip on manual-override. That lets the host drag/zoom/pitch
    // the map freely; the queue keeps growing in the background
    // and resumes when the host clicks the Pause / Free-Look
    // toggles back off. Previously this exit'd the whole teleport,
    // which surprised users who just wanted to peek at a nearby
    // building. The Exit Teleport button + Escape remain the
    // explicit "I'm done" paths.
    const handler = (e: any) => {
      if (!e || !e.originalEvent) return;             // programmatic, ignore
      if (phaseRef.current === 'entering') return;    // ignore the first big swoop
      if (phaseRef.current === 'exiting')  return;    // already restoring
      if (manualOverrideRef.current && pausedRef.current) return;   // already paused + free-look
      if (!manualOverrideRef.current) setManualOverrideSync(true);
      if (!pausedRef.current) {
        pausedRef.current = true;
        setPaused(true);
      }
    };
    map.on('dragstart',   handler);
    map.on('zoomstart',   handler);
    map.on('pitchstart',  handler);
    map.on('rotatestart', handler);
    return () => {
      try { map.off('dragstart',   handler); } catch { /* swallow */ }
      try { map.off('zoomstart',   handler); } catch { /* swallow */ }
      try { map.off('pitchstart',  handler); } catch { /* swallow */ }
      try { map.off('rotatestart', handler); } catch { /* swallow */ }
    };
  }, [enabled, map, setManualOverrideSync]);

  // ── Unmount cleanup ────────────────────────────────────────────────
  useEffect(() => () => {
    clearDisposers();
    try { map?.stop(); } catch { /* swallow */ }
    removeRouteLine();
    remove3DBuildingsLayer(map);
  }, [map, clearDisposers, removeRouteLine]);

  // Suppress unused warnings for helpers only consumed by future
  // wiring (publishPhase is currently invoked via the auto-publish
  // useEffect; keep it on the API surface so a manual call site is
  // a one-liner if we add explicit flush points later).
  void publishPhase;

  return {
    pendingAgentId,
    selectedAgentId,
    enabled,
    phase,
    countdownSeconds,
    currentStopIndex,
    manualOverride,
    nextDestination,
    queueLength,
    selectedAgent,
    pendingPanel,

    displayedAgentPosition,
    currentStopName,
    nextStopName,

    paused,
    hostPaused,

    role,
    selfId,
    hostId,
    hostWalletPk,
    viewers,

    requestPanel,
    dismissPanel,
    start,
    exit,
    setManualOverride: setManualOverrideSync,
    enqueuePlace,
    enqueueLngLat,
    setPaused: setPausedSync,
    setMapStyle: setMapStyleSync,
    originalMapStyle,
    currentMapStyle: mapStyle ?? null,
  };
}
